import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Sparkles, 
  MessageCircle, 
  Globe, 
  FileText, 
  ArrowRight, 
  Play,
  Star,
  Users,
  Zap,
  Heart,
  TrendingUp,
  Award,
  ChevronRight
} from 'lucide-react'
import { CreationEntry } from '../components/CreationEntry'
import { useWorldSimulatorStore } from '../stores/worldSimulatorStore'
import { usePlayCreateStore } from '../stores/playCreateStore'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import playCreateStorageService from '../services/playCreateStorageService'
import { WorldCard } from '../components/WorldCard'
import { CharacterCardWorkshop } from '../components/CharacterCardWorkshop'

export const LandingPage = () => {
  const navigate = useNavigate()
  const { loadWorlds } = useWorldSimulatorStore()
  const { loadCharacters } = usePlayCreateStore()
  
  const [featuredWorlds, setFeaturedWorlds] = useState([])
  const [featuredCharacters, setFeaturedCharacters] = useState([])
  const [characterStates, setCharacterStates] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState({
    totalWorlds: 0,
    totalCharacters: 0,
    totalUsers: 0
  })

  // 加载精选内容
  useEffect(() => {
    const loadFeaturedContent = async () => {
      try {
        setIsLoading(true)
        
        const [worldsData, charactersData] = await Promise.all([
          loadWorlds().then(() => worldSimulatorStorageService.getAllWorlds()),
          loadCharacters().then(() => playCreateStorageService.getAllCharacterProfiles())
        ])

        // 筛选已发布的作品
        const publicWorlds = (worldsData || []).filter(w => w.isPublic || w.status === 'published')
        const publicCharacters = (charactersData || []).filter(c => c.isPublic || c.status === 'published')
        
        // 按热门度排序，取前6个
        const sortedWorlds = publicWorlds
          .sort((a, b) => (b.playCount || 0) - (a.playCount || 0))
          .slice(0, 6)
        
        const sortedCharacters = publicCharacters
          .sort((a, b) => (b.totalChatCount || 0) - (a.totalChatCount || 0))
          .slice(0, 6)
        
        setFeaturedWorlds(sortedWorlds)
        setFeaturedCharacters(sortedCharacters)
        
        // 加载角色状态
        if (sortedCharacters.length > 0) {
          const states = {}
          await Promise.all(
            sortedCharacters.map(async (char) => {
              try {
                const state = await playCreateStorageService.getCharacterState(char.id)
                states[char.id] = state
              } catch (error) {
                states[char.id] = null
              }
            })
          )
          setCharacterStates(states)
        }
        
        setStats({
          totalWorlds: publicWorlds.length,
          totalCharacters: publicCharacters.length,
          totalUsers: 0 // 待实现
        })
      } catch (error) {
        console.error('加载精选内容失败:', error)
      } finally {
        setIsLoading(false)
      }
    }
    
    loadFeaturedContent()
  }, [loadWorlds, loadCharacters])

  const features = [
    {
      icon: Sparkles,
      title: 'AI 智能创作',
      description: '借助强大的 AI 技术，轻松创作故事、角色和世界',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: MessageCircle,
      title: '角色互动',
      description: '与 AI 角色进行深度对话，体验沉浸式互动',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Globe,
      title: '世界探索',
      description: '进入精心构建的虚拟世界，体验丰富剧情',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: FileText,
      title: '故事创作',
      description: '创作互动式故事，让读者成为故事的主角',
      color: 'from-orange-500 to-red-500'
    }
  ]

  const handleCardClick = (item, type) => {
    if (type === 'world') {
      navigate(`/content/world/${item.worldId || item.id}`)
    } else if (type === 'character') {
      navigate(`/content/character/${item.id}`)
    }
  }

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero 区域 */}
      <section className="relative overflow-hidden min-h-[600px] flex items-center">
        {/* 动态背景 - 使用多层渐变和动画 */}
        <div className="absolute inset-0">
          {/* 主背景渐变 */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-secondary/20 via-purple-500/20 to-pink-500/20"></div>
          
          {/* 动态光效 */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          
          {/* 网格背景 */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          ></div>
          
          {/* 底部渐变遮罩 */}
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-dark-900 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          <div className="text-center">
            {/* 标题 - 增强可读性 */}
            <h1 className="text-6xl md:text-8xl font-black mb-6 tracking-tight relative">
              <span className="relative inline-block">
                {/* 文字描边层 - 增强对比度 */}
                <span 
                  className="absolute inset-0 text-white"
                  style={{ 
                    WebkitTextStroke: '3px rgba(0, 0, 0, 0.5)',
                    textShadow: '0 0 20px rgba(0, 0, 0, 0.8), 0 0 40px rgba(0, 0, 0, 0.6)'
                  }}
                >
                  AltVerse
                </span>
                {/* 主文字 - 清晰可见 */}
                <span 
                  className="relative bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent"
                  style={{ 
                    filter: 'drop-shadow(0 0 10px rgba(6, 182, 212, 0.8)) drop-shadow(0 0 20px rgba(139, 92, 246, 0.6))',
                    WebkitTextStroke: '0.5px rgba(255, 255, 255, 0.3)'
                  }}
                >
                  AltVerse
                </span>
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white mb-4 max-w-3xl mx-auto font-medium drop-shadow-lg">
              开启你的 AI 创作之旅
            </p>
            <p className="text-lg text-dark-200 mb-12 max-w-2xl mx-auto drop-shadow-md">
              创作故事、创建角色、构建世界，用 AI 释放无限创意
            </p>

            {/* CTA 按钮 - 只保留一个 */}
            <div className="flex justify-center mb-16">
              <button
                onClick={() => navigate('/explore')}
                className="group px-10 py-5 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold text-lg shadow-2xl shadow-primary/40 hover:shadow-primary/60 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105 transform"
              >
                开始探索
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* 统计数据 */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{stats.totalWorlds}+</div>
                <div className="text-sm text-dark-400">精彩世界</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{stats.totalCharacters}+</div>
                <div className="text-sm text-dark-400">AI 角色</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{stats.totalUsers}+</div>
                <div className="text-sm text-dark-400">创作者</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 创作入口 */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 mb-24 relative z-10">
        <CreationEntry />
      </section>

      {/* 核心功能 */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            强大的创作工具
          </h2>
          <p className="text-xl text-dark-300 max-w-2xl mx-auto">
            一站式 AI 创作平台，满足你的所有创作需求
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <div
                key={index}
                className="group relative bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/20"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-dark-400">{feature.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* 精选内容 */}
      {!isLoading && (featuredWorlds.length > 0 || featuredCharacters.length > 0) && (
        <>
          {/* 精选世界 */}
          {featuredWorlds.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3">
                    <Globe className="w-8 h-8 text-emerald-400" />
                    精选世界
                  </h2>
                  <p className="text-dark-400">探索最受欢迎的世界</p>
                </div>
                <button
                  onClick={() => navigate('/explore?tab=worlds')}
                  className="flex items-center gap-2 px-6 py-3 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-white transition-colors"
                >
                  查看更多
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredWorlds.slice(0, 6).map((world, index) => (
                  <WorldCard
                    key={world.worldId || world.id}
                    world={world}
                    onClick={() => handleCardClick(world, 'world')}
                    index={index}
                  />
                ))}
              </div>
            </section>
          )}

          {/* 精选角色 */}
          {featuredCharacters.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3">
                    <MessageCircle className="w-8 h-8 text-cyan-400" />
                    热门角色
                  </h2>
                  <p className="text-dark-400">与最受欢迎的角色互动</p>
                </div>
                <button
                  onClick={() => navigate('/explore?tab=characters')}
                  className="flex items-center gap-2 px-6 py-3 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-white transition-colors"
                >
                  查看更多
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredCharacters.slice(0, 6).map((character, index) => (
                  <CharacterCardWorkshop
                    key={character.id}
                    character={character}
                    characterState={characterStates[character.id]}
                    onClick={() => handleCardClick(character, 'character')}
                    index={index}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* 平台特色 */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            为什么选择 AltVerse？
          </h2>
          <p className="text-xl text-dark-300 max-w-2xl mx-auto">
            专业的 AI 创作平台，让创作变得简单有趣
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">快速创作</h3>
            <p className="text-dark-400">AI 辅助创作，几分钟完成作品</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">社区支持</h3>
            <p className="text-dark-400">与创作者交流，分享你的作品</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">持续更新</h3>
            <p className="text-dark-400">不断优化功能，带来更好体验</p>
          </div>
        </div>
      </section>

      {/* 底部 CTA */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-purple-500/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            准备好开始创作了吗？
          </h2>
          <p className="text-xl text-dark-300 mb-8">
            加入 AltVerse，开启你的 AI 创作之旅
          </p>
          <button
            onClick={() => navigate('/explore')}
            className="px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold text-lg shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/50 transition-all duration-300 inline-flex items-center gap-2"
          >
            立即开始
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  )
}

